import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Save, 
  RefreshCw, 
  AlertTriangle, 
  X, 
  CheckCircle,
  Building2,
  Upload,
  Plus,
  Trash2,
  Pencil,
  Image as ImageIcon,
  ArrowLeft,
  CreditCard,
  MapPin,
  User,
} from 'lucide-react';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import SearchableSelect from '../ui/SearchableSelect';
import LocationAutocomplete from '../ui/LocationAutocomplete';

const PAYING_TYPE_OPTIONS = [
  { Id: 'Daily Payments', Name: 'Daily Payments' },
  { Id: 'Scheduled Payments', Name: 'Scheduled Payments' },
  { Id: 'On Demand Payments', Name: 'On Demand Payments' },
];

const FIELD =
  'w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20';

const FIELD_DISABLED = `${FIELD} cursor-not-allowed bg-gray-50 opacity-60`;

function FormField({ label, required, children, className = '' }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      {label ? (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required ? <span className="text-red-500"> *</span> : null}
        </label>
      ) : null}
      {children}
    </div>
  );
}

function SectionCard({ title, description, children }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm md:p-6">
      {(title || description) && (
        <div className="mb-5 border-b border-gray-100 pb-4">
          {title ? <h3 className="text-base font-semibold text-gray-900">{title}</h3> : null}
          {description ? <p className="mt-1 text-sm text-gray-500">{description}</p> : null}
        </div>
      )}
      {children}
    </div>
  );
}

const createEmptyAddressDraft = () => ({
  POBOX: '',
  Address: '',
  ContactNo: '',
  StoreNo: '',
  IsDefault: false,
  IsActive: true,
  LocationId: '',
  EmirateId: '',
  Location: { Name: '' },
  LocationUrl: '',
});

const AddClient = () => {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [emirates, setEmirates] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [addressDraft, setAddressDraft] = useState(() => createEmptyAddressDraft());
  /** `null` = add new address; otherwise global index in `client.ClientAddresses` being edited. */
  const [editingAddressIndex, setEditingAddressIndex] = useState(null);

  // Client form data
  const [client, setClient] = useState({
    Id: 0,
    Name: '',
    ClientNo: '',
    MobileNo1: '',
    MobileNo2: '',
    TelNo: '',
    Company: '',
    DeliveryCharges: 0,
    EmailId: '',
    Remarks: '',
    Photo: '',
    BankName: '',
    BankAccNo: '',
    BankAccName: '',
    BranchName: '',
    SwiftCode: '',
    IBANNO: '',
    PayingType: '',
    PayMode: 'CASH',
    ResponsibleById: '',
    IsArabic: false,
    IsZeroCharge: false,
    Date: new Date().toISOString().split('T')[0],
    ClientAddresses: []
  });

  useEffect(() => {
    const initialize = async () => {
      await loadDropdowns();
      if (clientId && parseInt(clientId) > 0) {
        await loadClient(parseInt(clientId));
      } else {
        // Get next client number
        try {
          const response = await MainService.GetNextClientNo();
          if (response) {
            setClient(prev => ({ ...prev, ClientNo: response }));
          }
        } catch (err) {
          console.error('Error getting next client number:', err);
        }
      }
    };
    initialize();
  }, [clientId]);

  // Toast notification effects
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadDropdowns = async () => {
    try {
      const [emiratesRes, driversRes] = await Promise.all([
        MainService.EmiratesInDropDown(),
        MainService.EmployeesInDropDown()
      ]);
      setEmirates(emiratesRes || []);
      setDrivers(driversRes || []);
    } catch (err) {
      console.error('Error loading dropdowns:', err);
      setError('Failed to load dropdown data');
    }
  };

  const loadClient = async (id) => {
    try {
      setLoading(true);
      const response = await MainService.ClientById(id);
      if (response) {
        setClient({
          ...response,
          Date: response.Date ? new Date(response.Date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          ClientAddresses: response.ClientAddresses || [],
          DeliveryCharges: response.DeliveryCharges || 0,
          IsArabic: response.IsArabic || false,
          IsZeroCharge: response.IsZeroCharge || false,
          BranchName: response.BranchName || '',
          SwiftCode: response.SwiftCode || '',
          IBANNO: response.IBANNO || '',
          PayingType: response.PayingType || '',
        });
        
        if (response.Photo) {
          const photoUrl = response.Photo && response.Photo.startsWith('http') 
            ? response.Photo 
            : `${getApiBaseUrl()}${response.Photo?.startsWith('/') ? response.Photo.slice(1) : response.Photo}`;
          setPhotoPreview(photoUrl);
        }
        setAddressDraft(createEmptyAddressDraft());
        setEditingAddressIndex(null);
      }
    } catch (err) {
      console.error('Error loading client:', err);
      setError('Failed to load client data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setClient(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
        setClient(prev => ({ ...prev, Photo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const ensureAtLeastOneDefault = (addresses) => {
    const next = [...addresses];
    const activeIdx = next.map((a, i) => (a.IsActive ? i : -1)).filter((i) => i >= 0);
    if (activeIdx.length === 0) return next;
    if (!activeIdx.some((i) => next[i].IsDefault)) {
      const first = activeIdx[0];
      next[first] = { ...next[first], IsDefault: true };
    }
    return next;
  };

  const commitAddressFromDraft = () => {
    if (!addressDraft.EmirateId || !addressDraft.LocationId || !addressDraft.Address?.trim()) {
      setError('Emirate, Location, and Address are required');
      return;
    }
    setError('');

    if (editingAddressIndex !== null) {
      const idx = editingAddressIndex;
      setClient((prev) => {
        if (idx < 0 || idx >= prev.ClientAddresses.length) return prev;
        const prevRow = prev.ClientAddresses[idx];
        if (!prevRow?.IsActive) return prev;
        const updated = {
          ...prevRow,
          POBOX: addressDraft.POBOX || '',
          Address: addressDraft.Address || '',
          ContactNo: addressDraft.ContactNo || '',
          StoreNo: addressDraft.StoreNo || '',
          EmirateId: addressDraft.EmirateId,
          LocationId: addressDraft.LocationId,
          Location: addressDraft.Location?.Name ? { Name: addressDraft.Location.Name } : { Name: '' },
          LocationUrl: addressDraft.LocationUrl || '',
          IsDefault: !!addressDraft.IsDefault,
          IsActive: true,
        };
        let addresses = [...prev.ClientAddresses];
        if (updated.IsDefault) {
          addresses = addresses.map((a, i) =>
            i === idx ? updated : a.IsActive ? { ...a, IsDefault: false } : a
          );
        } else {
          addresses[idx] = updated;
        }
        addresses = ensureAtLeastOneDefault(addresses);
        return { ...prev, ClientAddresses: addresses };
      });
      setEditingAddressIndex(null);
      setAddressDraft(createEmptyAddressDraft());
      return;
    }

    const activeBefore = client.ClientAddresses.filter((a) => a.IsActive);
    const isFirst = activeBefore.length === 0;
    const newRow = {
      ...addressDraft,
      IsActive: true,
      IsDefault: isFirst ? true : !!addressDraft.IsDefault,
    };
    setClient((prev) => {
      let addresses = [...prev.ClientAddresses];
      if (newRow.IsDefault) {
        addresses = addresses.map((a) => (a.IsActive ? { ...a, IsDefault: false } : a));
      }
      addresses.push(newRow);
      addresses = ensureAtLeastOneDefault(addresses);
      return { ...prev, ClientAddresses: addresses };
    });
    setAddressDraft(createEmptyAddressDraft());
  };

  const startEditAddress = (globalIndex) => {
    const row = client.ClientAddresses[globalIndex];
    if (!row?.IsActive) return;
    setAddressDraft({
      POBOX: row.POBOX || '',
      Address: row.Address || '',
      ContactNo: row.ContactNo || '',
      StoreNo: row.StoreNo || '',
      IsDefault: !!row.IsDefault,
      IsActive: true,
      LocationId: row.LocationId || '',
      EmirateId: row.EmirateId || '',
      Location: row.Location?.Name ? { Name: row.Location.Name } : { Name: '' },
      LocationUrl: row.LocationUrl || '',
    });
    setEditingAddressIndex(globalIndex);
    setError('');
  };

  const cancelAddressEdit = () => {
    setEditingAddressIndex(null);
    setAddressDraft(createEmptyAddressDraft());
  };

  const removeAddress = (index) => {
    setEditingAddressIndex(null);
    setAddressDraft(createEmptyAddressDraft());
    setClient((prev) => {
      const addresses = [...prev.ClientAddresses];
      addresses[index] = { ...addresses[index], IsActive: false };
      return {
        ...prev,
        ClientAddresses: ensureAtLeastOneDefault(addresses),
      };
    });
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const deleteWithConfirm = async () => {
    try {
      setLoading(true);
      const response = await MainService.DeleteClient(client.Id);
      
      if (response === 'Success' || response === true) {
        setSuccess('Client deleted successfully');
        setTimeout(() => {
          navigate('/home/accounts/clients');
        }, 1500);
      } else {
        setError(response || 'Failed to delete client');
      }
    } catch (err) {
      console.error('Error deleting client:', err);
      setError('Failed to delete client');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!client.Name || client.Name.trim() === '') {
      setError('Enter Name');
      return;
    }
    if (!client.MobileNo1 || client.MobileNo1.trim() === '') {
      setError('Enter Mobile Number');
      return;
    }
    if (client.DeliveryCharges === 0 && !client.IsZeroCharge) {
      setError('Enter Delivery Charge');
      return;
    }

    const activeAddresses = client.ClientAddresses.filter(a => a.IsActive);
    if (activeAddresses.length === 0) {
      setError('Add at least one address');
      return;
    }

    let addressInvalid = false;
    let defaultFound = false;
    let defaultCount = 0;

    activeAddresses.forEach(addr => {
      if (addr.IsDefault) {
        defaultFound = true;
        defaultCount++;
      }
      if (!addr.EmirateId || !addr.LocationId || !addr.Address || addr.Address.trim() === '') {
        addressInvalid = true;
      }
    });

    if (addressInvalid) {
      setError('Invalid Address Found | Emirate, Location, Address Compulsory');
      return;
    }

    if (!defaultFound) {
      setError('Make One Address As Default');
      return;
    }

    if (defaultCount !== 1) {
      setError('Make One Address As Default');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await MainService.SaveClient(client);
      
      if (response === 'Success' || response === true) {
        setSuccess('Client saved successfully');
        setTimeout(() => {
          navigate('/home/accounts/clients');
        }, 1500);
      } else {
        setError(response || 'Failed to save client');
      }
    } catch (err) {
      console.error('Error saving client:', err);
      setError('Failed to save client');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setClient({
      Id: 0,
      Name: '',
      ClientNo: '',
      MobileNo1: '',
      MobileNo2: '',
      TelNo: '',
      Company: '',
      DeliveryCharges: 0,
      EmailId: '',
      Remarks: '',
      Photo: '',
      BankName: '',
      BankAccNo: '',
      BankAccName: '',
      BranchName: '',
      SwiftCode: '',
      IBANNO: '',
      PayingType: '',
      PayMode: 'CASH',
      ResponsibleById: '',
      IsArabic: false,
      IsZeroCharge: false,
      Date: new Date().toISOString().split('T')[0],
      ClientAddresses: []
    });
    setPhotoPreview(null);
    setPhotoFile(null);
    setAddressDraft(createEmptyAddressDraft());
    setEditingAddressIndex(null);
  };

  const setActiveAddressDefault = (globalIndex) => {
    setClient((prev) => ({
      ...prev,
      ClientAddresses: prev.ClientAddresses.map((a, idx) =>
        !a.IsActive ? a : { ...a, IsDefault: idx === globalIndex }
      ),
    }));
  };

  const getEmirateName = (id) => {
    const e = emirates.find((x) => x.Id === id);
    return e ? e.Name || e.Number || '—' : '—';
  };

  const tabs = [
    { id: 0, label: 'Basic', icon: User },
    { id: 1, label: 'Payment', icon: CreditCard },
    { id: 2, label: 'Address', icon: MapPin },
  ];

  const isEdit = clientId && parseInt(clientId, 10) > 0;
  const activeAddresses = client.ClientAddresses.filter((a) => a.IsActive);

  return (
    <div className="space-y-5 pb-8">
      {error && (
        <div className="fixed top-4 right-4 z-50 flex min-w-[300px] items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 shadow-lg">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1 text-sm">{error}</span>
          <button type="button" onClick={() => setError('')} className="text-red-500 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 z-50 flex min-w-[300px] items-center gap-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-green-700 shadow-lg">
          <CheckCircle className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1 text-sm">{success}</span>
          <button type="button" onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Page header */}
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="bg-gradient-to-r from-primary/10 via-white to-emerald-50/40 px-5 py-5 md:px-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-3">
              <button
                type="button"
                onClick={() => navigate('/home/accounts/clients')}
                className="mt-0.5 rounded-xl border border-gray-200 bg-white p-2.5 text-gray-600 shadow-sm hover:bg-gray-50"
                title="Back to clients"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-primary/10 p-3 text-primary">
                  <Building2 className="h-7 w-7" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    {isEdit ? 'Edit Client' : 'Add Client'}
                  </h1>
                  <p className="mt-1 text-sm text-gray-500">
                    {isEdit ? 'Update client profile, payment, and addresses' : 'Create a new client record'}
                  </p>
                  {client.ClientNo ? (
                    <span className="mt-2 inline-flex rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
                      {client.ClientNo}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {loading && <RefreshCw className="h-5 w-5 animate-spin text-primary" />}
              {client.Id > 0 && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
              )}
              <button
                type="button"
                onClick={handleReset}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw className="h-4 w-4" />
                Reset
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm shadow-primary/20 hover:bg-green-700 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                Save Client
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-t border-gray-100 bg-gray-50/80 px-3 py-2 md:px-4">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all ${
                    active
                      ? 'bg-white text-primary shadow-sm ring-1 ring-primary/20'
                      : 'text-gray-600 hover:bg-white/80 hover:text-gray-900'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab content */}
      <div>
        {/* Basic Tab */}
        {activeTab === 0 && (
          <SectionCard
            title="Basic Information"
            description="Client profile, contact details, and preferences"
          >
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-[220px_1fr]">
              <FormField label="Photo">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                  id="photo-upload"
                />
                <label htmlFor="photo-upload" className="block cursor-pointer">
                  <div className="group relative mx-auto flex aspect-square w-full max-w-[220px] items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 transition-colors hover:border-primary/40 hover:bg-primary/5">
                    {photoPreview ? (
                      <>
                        <div
                          className="absolute inset-0 bg-cover bg-center opacity-30 blur-md"
                          style={{ backgroundImage: `url(${photoPreview})` }}
                        />
                        <img
                          src={photoPreview}
                          alt="Client"
                          className="relative z-10 h-full w-full object-contain p-2"
                        />
                        <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/0 transition-all group-hover:bg-black/30">
                          <Upload className="h-8 w-8 text-white opacity-0 transition-opacity group-hover:opacity-100" />
                        </div>
                      </>
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-gray-400">
                        <ImageIcon className="h-10 w-10" />
                        <span className="text-xs font-medium">Upload photo</span>
                      </div>
                    )}
                  </div>
                </label>
              </FormField>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                <FormField label="Name" required>
                  <input
                    type="text"
                    value={client.Name}
                    onChange={(e) => handleInputChange('Name', e.target.value)}
                    className={FIELD}
                    placeholder="Client name"
                    required
                  />
                </FormField>
                <FormField label="Mobile No1" required>
                  <input
                    type="text"
                    value={client.MobileNo1}
                    onChange={(e) => handleInputChange('MobileNo1', e.target.value)}
                    className={FIELD}
                    placeholder="Primary mobile"
                    required
                  />
                </FormField>
                <FormField label="Mobile No2">
                  <input
                    type="text"
                    value={client.MobileNo2}
                    onChange={(e) => handleInputChange('MobileNo2', e.target.value)}
                    className={FIELD}
                    placeholder="Secondary mobile"
                  />
                </FormField>
                <FormField label="Tel No">
                  <input
                    type="text"
                    value={client.TelNo}
                    onChange={(e) => handleInputChange('TelNo', e.target.value)}
                    className={FIELD}
                    placeholder="Landline"
                  />
                </FormField>
                <FormField label="Email">
                  <input
                    type="email"
                    value={client.EmailId}
                    onChange={(e) => handleInputChange('EmailId', e.target.value)}
                    className={FIELD}
                    placeholder="email@example.com"
                  />
                </FormField>
                <FormField label="Company">
                  <input
                    type="text"
                    value={client.Company}
                    onChange={(e) => handleInputChange('Company', e.target.value)}
                    className={FIELD}
                    placeholder="Company name"
                  />
                </FormField>
                <FormField label="Remarks" className="md:col-span-2 xl:col-span-1">
                  <input
                    type="text"
                    value={client.Remarks}
                    onChange={(e) => handleInputChange('Remarks', e.target.value)}
                    className={FIELD}
                    placeholder="Notes"
                  />
                </FormField>
                <FormField label="Responsible By">
                  <SearchableSelect
                    value={client.ResponsibleById}
                    onChange={(value) => handleInputChange('ResponsibleById', value)}
                    options={drivers}
                    placeholder="Select driver"
                  />
                </FormField>
                <div className="flex flex-wrap items-end gap-4 md:col-span-2 xl:col-span-3">
                  <label className="inline-flex cursor-pointer items-center gap-2.5 rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5">
                    <input
                      type="checkbox"
                      checked={client.IsArabic || false}
                      onChange={(e) => handleInputChange('IsArabic', e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className="text-sm font-medium text-gray-700">Arabic client</span>
                  </label>
                  <label className="inline-flex cursor-pointer items-center gap-2.5 rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5">
                    <input
                      type="checkbox"
                      checked={client.IsZeroCharge || false}
                      onChange={(e) => handleInputChange('IsZeroCharge', e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className="text-sm font-medium text-gray-700">Free delivery</span>
                  </label>
                </div>
              </div>
            </div>
          </SectionCard>
        )}

        {/* Payment Tab */}
        {activeTab === 1 && (
          <div className="space-y-5">
            <SectionCard
              title="Bank Details"
              description="Bank account and payment configuration"
            >
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
                <FormField label="Bank Name">
                  <input
                    type="text"
                    value={client.BankName}
                    onChange={(e) => handleInputChange('BankName', e.target.value)}
                    className={FIELD}
                    placeholder="Bank name"
                  />
                </FormField>
                <FormField label="Bank Acc No">
                  <input
                    type="text"
                    value={client.BankAccNo}
                    onChange={(e) => handleInputChange('BankAccNo', e.target.value)}
                    className={FIELD}
                    placeholder="Account number"
                  />
                </FormField>
                <FormField label="Bank Acc Name">
                  <input
                    type="text"
                    value={client.BankAccName}
                    onChange={(e) => handleInputChange('BankAccName', e.target.value)}
                    className={FIELD}
                    placeholder="Account holder name"
                  />
                </FormField>
                <FormField label="Payment Mode">
                  <SearchableSelect
                    value={client.PayMode}
                    onChange={(value) => handleInputChange('PayMode', value)}
                    options={[
                      { Id: 'CASH', Name: 'CASH' },
                      { Id: 'BANK', Name: 'BANK' },
                    ]}
                    placeholder="Select mode"
                  />
                </FormField>
                <FormField label="Branch Name">
                  <input
                    type="text"
                    value={client.BranchName || ''}
                    onChange={(e) => handleInputChange('BranchName', e.target.value)}
                    className={FIELD}
                    placeholder="Branch name"
                  />
                </FormField>
                <FormField label="Swift Code">
                  <input
                    type="text"
                    value={client.SwiftCode || ''}
                    onChange={(e) => handleInputChange('SwiftCode', e.target.value)}
                    className={FIELD}
                    placeholder="SWIFT / BIC"
                  />
                </FormField>
                <FormField label="IBAN No">
                  <input
                    type="text"
                    value={client.IBANNO || ''}
                    onChange={(e) => handleInputChange('IBANNO', e.target.value)}
                    className={FIELD}
                    placeholder="IBAN"
                  />
                </FormField>
                <FormField label="Paying Type">
                  <SearchableSelect
                    value={client.PayingType || ''}
                    onChange={(value) => handleInputChange('PayingType', value)}
                    options={PAYING_TYPE_OPTIONS}
                    placeholder="Select paying type"
                  />
                </FormField>
              </div>
            </SectionCard>

            <SectionCard
              title="Delivery Charges"
              description="Default delivery charge for this client"
            >
              <div className="max-w-sm">
                <FormField label="Delivery Charges" required>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={client.DeliveryCharges}
                    onChange={(e) => handleInputChange('DeliveryCharges', parseFloat(e.target.value) || 0)}
                    disabled={client.IsZeroCharge}
                    className={client.IsZeroCharge ? FIELD_DISABLED : FIELD}
                    required
                  />
                  {client.IsZeroCharge ? (
                    <p className="mt-1.5 text-xs text-gray-500">Disabled because free delivery is enabled on Basic tab.</p>
                  ) : null}
                </FormField>
              </div>
            </SectionCard>
          </div>
        )}

        {/* Address Tab */}
        {activeTab === 2 && (
          <div className="space-y-5">
            <SectionCard
              title={editingAddressIndex !== null ? 'Edit address' : 'Add address'}
              description={
                editingAddressIndex !== null
                  ? 'Update the fields below, then save changes.'
                  : 'Enter details and add to the list below.'
              }
            >
              <div
                className={`rounded-xl border p-4 md:p-5 ${
                  editingAddressIndex !== null
                    ? 'border-primary/30 bg-primary/5 ring-1 ring-primary/20'
                    : 'border-gray-100 bg-gray-50/60'
                }`}
              >
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
                  <SearchableSelect
                    value={addressDraft.EmirateId}
                    onChange={(value) =>
                      setAddressDraft((prev) => ({
                        ...prev,
                        EmirateId: value,
                        LocationId: '',
                        Location: { Name: '' },
                      }))
                    }
                    options={emirates}
                    placeholder="Select emirate"
                    required
                    label="Emirate"
                  />
                  <LocationAutocomplete
                    value={addressDraft.Location}
                    onChange={(location) =>
                      setAddressDraft((prev) => ({
                        ...prev,
                        LocationId: location?.Id || '',
                        Location: { Name: location?.Name || '' },
                      }))
                    }
                    emirateId={addressDraft.EmirateId}
                    placeholder="Search location..."
                    disabled={!addressDraft.EmirateId}
                    required
                    label="Location"
                  />
                  <FormField label="Contact No">
                    <input
                      type="text"
                      value={addressDraft.ContactNo || ''}
                      onChange={(e) => setAddressDraft((prev) => ({ ...prev, ContactNo: e.target.value }))}
                      className={FIELD}
                      placeholder="Contact number"
                    />
                  </FormField>
                  <div className="flex flex-col justify-end">
                    <label className="inline-flex cursor-pointer items-center gap-2.5 rounded-xl border border-gray-200 bg-white px-4 py-2.5">
                      <input
                        type="checkbox"
                        checked={!!addressDraft.IsDefault}
                        disabled={editingAddressIndex === null && activeAddresses.length === 0}
                        onChange={(e) => setAddressDraft((prev) => ({ ...prev, IsDefault: e.target.checked }))}
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-50"
                      />
                      <span className="text-sm font-medium text-gray-700">Set as default</span>
                    </label>
                    {editingAddressIndex === null && activeAddresses.length === 0 ? (
                      <p className="mt-1.5 text-xs text-gray-500">The first address is always the default.</p>
                    ) : null}
                  </div>
                </div>
                <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-5">
                  <FormField label="Address" required className="md:col-span-2">
                    <textarea
                      rows={2}
                      value={addressDraft.Address || ''}
                      onChange={(e) => setAddressDraft((prev) => ({ ...prev, Address: e.target.value }))}
                      className={`${FIELD} resize-none`}
                      placeholder="Full address"
                    />
                  </FormField>
                  <FormField label="Location URL">
                    <input
                      type="text"
                      value={addressDraft.LocationUrl || ''}
                      onChange={(e) => setAddressDraft((prev) => ({ ...prev, LocationUrl: e.target.value }))}
                      className={FIELD}
                      placeholder="Google Maps link"
                    />
                  </FormField>
                  <FormField label="PO Box">
                    <input
                      type="text"
                      value={addressDraft.POBOX || ''}
                      onChange={(e) => setAddressDraft((prev) => ({ ...prev, POBOX: e.target.value }))}
                      className={FIELD}
                    />
                  </FormField>
                  <FormField label="Store No">
                    <input
                      type="text"
                      value={addressDraft.StoreNo || ''}
                      onChange={(e) => setAddressDraft((prev) => ({ ...prev, StoreNo: e.target.value }))}
                      className={FIELD}
                    />
                  </FormField>
                </div>
                <div className="mt-5 flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-4">
                  {editingAddressIndex !== null && (
                    <button
                      type="button"
                      onClick={cancelAddressEdit}
                      className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={commitAddressFromDraft}
                    className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-green-700"
                  >
                    {editingAddressIndex !== null ? (
                      <>
                        <Pencil className="h-4 w-4" />
                        Save changes
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4" />
                        Add to list
                      </>
                    )}
                  </button>
                </div>
              </div>
            </SectionCard>

            <SectionCard
              title="Address list"
              description={`${activeAddresses.length} address${activeAddresses.length === 1 ? '' : 'es'} saved`}
            >
              <div className="overflow-hidden rounded-xl border border-gray-200">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="whitespace-nowrap px-4 py-3 font-semibold text-gray-600">#</th>
                        <th className="whitespace-nowrap px-4 py-3 font-semibold text-gray-600">Emirate</th>
                        <th className="min-w-[140px] px-4 py-3 font-semibold text-gray-600">Location</th>
                        <th className="min-w-[200px] px-4 py-3 font-semibold text-gray-600">Address</th>
                        <th className="whitespace-nowrap px-4 py-3 font-semibold text-gray-600">Contact</th>
                        <th className="whitespace-nowrap px-4 py-3 font-semibold text-gray-600">PO Box</th>
                        <th className="whitespace-nowrap px-4 py-3 font-semibold text-gray-600">Store No</th>
                        <th className="whitespace-nowrap px-4 py-3 font-semibold text-gray-600">Default</th>
                        <th className="whitespace-nowrap px-4 py-3 text-right font-semibold text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {activeAddresses.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="px-4 py-12 text-center">
                            <MapPin className="mx-auto mb-2 h-8 w-8 text-gray-300" />
                            <p className="text-sm text-gray-500">No addresses yet. Use the form above to add one.</p>
                          </td>
                        </tr>
                      ) : (
                        activeAddresses.map((address, displayIndex) => {
                          const globalIndex = client.ClientAddresses.findIndex((a) => a === address);
                          return (
                            <tr
                              key={`${globalIndex}-${displayIndex}`}
                              className={`align-top transition-colors hover:bg-gray-50/80 ${
                                editingAddressIndex === globalIndex ? 'bg-primary/5' : ''
                              }`}
                            >
                              <td className="whitespace-nowrap px-4 py-3 text-gray-500">{displayIndex + 1}</td>
                              <td className="whitespace-nowrap px-4 py-3 font-medium text-gray-900">{getEmirateName(address.EmirateId)}</td>
                              <td className="px-4 py-3 text-gray-900">{address.Location?.Name || '—'}</td>
                              <td className="max-w-xs px-4 py-3 text-gray-700">
                                <span className="line-clamp-3 whitespace-pre-wrap">{address.Address || '—'}</span>
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-gray-700">{address.ContactNo || '—'}</td>
                              <td className="whitespace-nowrap px-4 py-3 text-gray-700">{address.POBOX || '—'}</td>
                              <td className="whitespace-nowrap px-4 py-3 text-gray-700">{address.StoreNo || '—'}</td>
                              <td className="whitespace-nowrap px-4 py-3">
                                {address.IsDefault ? (
                                  <span className="inline-flex rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
                                    Default
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => globalIndex >= 0 && setActiveAddressDefault(globalIndex)}
                                    className="text-xs font-semibold text-primary hover:underline"
                                  >
                                    Set default
                                  </button>
                                )}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-right">
                                <div className="inline-flex items-center justify-end gap-1">
                                  <button
                                    type="button"
                                    onClick={() => globalIndex >= 0 && startEditAddress(globalIndex)}
                                    className="inline-flex items-center justify-center rounded-lg p-2 text-primary transition-colors hover:bg-primary/10"
                                    title="Edit address"
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => globalIndex >= 0 && removeAddress(globalIndex)}
                                    className="inline-flex items-center justify-center rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50"
                                    title="Remove address"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </SectionCard>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center gap-3">
              <div className="rounded-xl bg-red-50 p-2.5 text-red-600">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete client?</h3>
            </div>
            <p className="mb-6 text-sm text-gray-600">
              This will permanently remove the client and cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteDialogOpen(false)}
                className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={deleteWithConfirm}
                disabled={loading}
                className="rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddClient;
